/** A proactive offer the AI chat makes to start a guided skill. */
export const ChatNudgeId = {
  APPROVAL_AUTOMATION_SETUP: 'approval-automation-setup',
  VALIDATION_AUTOMATION_SETUP: 'validation-automation-setup',
  /** A sync fails, has gone stale, or its Bridge is offline. */
  INTEGRATION_TROUBLE: 'integration-trouble'
} as const;

export type ChatNudgeId = (typeof ChatNudgeId)[keyof typeof ChatNudgeId];

/**
 * Nudges that warn rather than offer: checked live on every trigger, offered again only for a
 * problem not shown before, never retired by "no thanks" and outside the one-offer-a-day limit.
 */
export const ALERT_CHAT_NUDGE_IDS: readonly ChatNudgeId[] = [ChatNudgeId.INTEGRATION_TROUBLE];

/** Whether the nudge is an alert. */
export const isAlertChatNudge = (id: string): boolean =>
  (ALERT_CHAT_NUDGE_IDS as readonly string[]).includes(id);

/** Where a nudge may surface. */
export const ChatNudgeChannel = {
  /** Closing the daily greeting. */
  GREETING: 'GREETING',
  /** Right after something the user did in the app, reported by the web-app. */
  SIGNAL: 'SIGNAL',
  /** When the chat opens, after the server noticed something in the account. */
  EVENT: 'EVENT'
} as const;

export type ChatNudgeChannel = (typeof ChatNudgeChannel)[keyof typeof ChatNudgeChannel];

/** What the user just did in the app — a hint only; every condition is checked on the server. */
export const ChatNudgeSignal = {
  /** The chat panel opened — offers what a server event queued (EVENT channel). */
  CHAT_OPENED: 'CHAT_OPENED',
  MANUAL_APPROVAL: 'MANUAL_APPROVAL',
  AUTOMATION_SETTINGS_OPENED: 'AUTOMATION_SETTINGS_OPENED'
} as const;

export type ChatNudgeSignal = (typeof ChatNudgeSignal)[keyof typeof ChatNudgeSignal];

/** Something the server noticed in the account that may warrant a nudge. */
export const ChatNudgeEvent = {
  /** People decided many approvals by hand while automation could take them. */
  APPROVALS_DECIDED_MANUALLY: 'APPROVALS_DECIDED_MANUALLY'
} as const;

export type ChatNudgeEvent = (typeof ChatNudgeEvent)[keyof typeof ChatNudgeEvent];

/** What happened to a nudge the user was shown. */
export const ChatNudgeOutcome = {
  SHOWN: 'SHOWN',
  /** The user started the guided turn. */
  ACCEPTED: 'ACCEPTED',
  /** "No thanks" — counts towards never offering it again. */
  DISMISSED: 'DISMISSED',
  /** "Later" — quiet for a while, never counted as a refusal. */
  SNOOZED: 'SNOOZED'
} as const;

export type ChatNudgeOutcome = (typeof ChatNudgeOutcome)[keyof typeof ChatNudgeOutcome];
